import {
  AssignableType,
  AssignableTypeEnum,
  BasketballTeamBasic,
  RequestBasePostTaskAutoAssignmentPreview,
  SelectOption,
  TaskAutoAssignmentAssignableUser,
  TaskAutoAssignmentPreviewEvent,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import API from '../../../api/api';

const FEATURE_KEY = 'tasks';

/************************************************************************************
 *                                    HELPERS
 * **********************************************************************************/
function getAssignableAsSelectOption(
  assignable: BasketballTeamBasic | TaskAutoAssignmentAssignableUser,
  mode: AssignableTypeEnum
): SelectOption {
  if (mode === AssignableType.USER) {
    const user = assignable as TaskAutoAssignmentAssignableUser;
    return {
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    } as SelectOption;
  } else {
    const team = assignable as BasketballTeamBasic;
    return {
      label: team.name,
      value: team.id,
    } as SelectOption;
  }
}

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface TasksState {
  wizard: {
    mode: AssignableTypeEnum;
    eventIds: string[];
    assignableIds: number[];
    basketballTeamIds: string[];
    options: {
      numberOfAssigneesPerEvent: number;
      considerPrevAssignments?: {
        season: boolean;
        from?: string;
        until?: string;
      };
    };
    assignables: BasketballTeamBasic[] | TaskAutoAssignmentAssignableUser[];
    preview: TaskAutoAssignmentPreviewEvent[];
  };
}

const initialState: TasksState = {
  wizard: {
    mode: AssignableType.USER,
    eventIds: [],
    assignableIds: [],
    basketballTeamIds: [],
    options: {
      numberOfAssigneesPerEvent: 1,
    },
    assignables: [],
    preview: [],
  },
};

const fetchAutoAssignmentAssignables = createAsyncThunk(
  'tasks/fetchAutoAssignmentAssignables',
  async (
    data: {
      taskId: number;
      type: AssignableTypeEnum;
      basketballTeamId?: number;
    },
    thunkAPI
  ) => {
    const response = await API.tasks().getAutoAssignmentsAssignables(
      data.taskId,
      data.type,
      data.basketballTeamId
    );
    return response?.data;
  }
);

const fetchAutoAssignmentPreview = createAsyncThunk(
  'tasks/fetchAutoAssignmentPreview',
  async (
    data: {
      taskId: number;
      requestData: RequestBasePostTaskAutoAssignmentPreview;
    },
    thunkAPI
  ) => {
    const response = await API.tasks().getAutoAssignmentsPreview(
      data.taskId,
      data.requestData
    );
    return response?.data;
  }
);

const tasksSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    resetWizard: (state) => {
      state.wizard = {
        ...initialState.wizard,
      };
    },
    setWizardEventIds: (state, action: PayloadAction<string[]>) => {
      state.wizard.eventIds = action.payload;
    },
    setWizardAssignablesIds: (state, action: PayloadAction<number[]>) => {
      state.wizard.assignableIds = action.payload;
    },
    setWizardMode: (state, action: PayloadAction<AssignableTypeEnum>) => {
      state.wizard.mode = action.payload;
      state.wizard.assignables = [];
      state.wizard.assignableIds = [];
    },
    setWizardNumberOfAssigneesPerEvent: (
      state,
      action: PayloadAction<number>
    ) => {
      state.wizard.options.numberOfAssigneesPerEvent = action.payload;
    },
    setWizardOptionsConsiderPrevAssignments: (
      state,
      action: PayloadAction<
        { season: boolean; from?: string; till?: string } | undefined
      >
    ) => {
      if (action?.payload?.season) {
        state.wizard.options.considerPrevAssignments = {
          season: action.payload.season,
          from: undefined,
          until: undefined,
        };
      } else if (action?.payload?.from || action?.payload?.till) {
        state.wizard.options.considerPrevAssignments = {
          season: false,
          from:
            action.payload.from ??
            state.wizard.options.considerPrevAssignments?.from,
          until:
            action.payload.till ??
            state.wizard.options.considerPrevAssignments?.until,
        };
      } else {
        state.wizard.options.considerPrevAssignments = undefined;
      }
    },
    setWizardPreviewAssignables: (
      state: TasksState,
      action: PayloadAction<{
        eventId: string;
        assignables: number[];
      }>
    ) => {
      const event = state.wizard.preview.find(
        (e) => e.id === action.payload.eventId
      );
      if (event) {
        event.assignments = (state.wizard.assignables as any)
          .filter(
            (a: BasketballTeamBasic | TaskAutoAssignmentAssignableUser) => {
              return action.payload.assignables.includes(a.id);
            }
          )
          .map((a: BasketballTeamBasic | TaskAutoAssignmentAssignableUser) => {
            return getAssignableAsSelectOption(a, state.wizard.mode);
          });
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchAutoAssignmentAssignables.fulfilled,
      (
        state: TasksState,
        action: PayloadAction<
          TaskAutoAssignmentAssignableUser[] | BasketballTeamBasic[]
        >
      ) => {
        state.wizard.assignables = action.payload;
        state.wizard.assignableIds = action.payload.map((a) => a.id);
      }
    );
    builder.addCase(
      fetchAutoAssignmentPreview.fulfilled,
      (
        state: TasksState,
        action: PayloadAction<TaskAutoAssignmentPreviewEvent[]>
      ) => {
        state.wizard.preview = action.payload;
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
const reducer = tasksSlice.reducer;
const actions = tasksSlice.actions;

const state = (rootState: any): TasksState => rootState[FEATURE_KEY];

const autoAssignmentWizardSelector = createSelector(state, (state) => {
  return state.wizard;
});

const autoAssignmentWizardAssignablesSelectOptionsSelector = createSelector(
  state,
  (state) => {
    return state.wizard.assignables.map((a) => {
      return getAssignableAsSelectOption(a, state.wizard.mode);
    });
  }
);

const autoAssignmentWizardNextButtonDisabledSelector = (
  stepKey: string,
  minNumberOfUsers?: number,
  maxNumberOfUsers?: number
) =>
  createSelector(state, (state) => {
    switch (stepKey) {
      case 'events':
        return state?.wizard?.eventIds?.length === 0;
      case 'users':
        return state?.wizard?.assignableIds?.length === 0;
      case 'options':
        if (state?.wizard?.mode === AssignableType.BASKETBALL_TEAM) {
          return state?.wizard?.options?.numberOfAssigneesPerEvent === 0;
        }
        return (
          state?.wizard?.options?.numberOfAssigneesPerEvent >
            (maxNumberOfUsers ?? 99) ||
          state?.wizard?.options?.numberOfAssigneesPerEvent <
            (minNumberOfUsers ?? 1)
        );
      default:
        return false;
    }
  });

export {
  actions,
  autoAssignmentWizardAssignablesSelectOptionsSelector,
  autoAssignmentWizardSelector,
  autoAssignmentWizardNextButtonDisabledSelector,
  fetchAutoAssignmentAssignables,
  fetchAutoAssignmentPreview,
  reducer,
};
